import {
  Col,
  Divider,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Modal,
  Tabs,
  Rate,
  Button,
  Badge,
  Alert,
} from 'antd';
import Link from 'antd/lib/typography/Link';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryParam, NumberParam } from 'use-query-params';
import { FlexBox } from '../../../../components/atoms/FlexBox';
import { CycleToggle } from '../../../../components/atoms/ItemToggle';
import { UserAvatar } from '../../../../components/atoms/UserAvatar';
import ReviewPreview from '../../../../components/molecules/ReviewPreview/ReviewPreview';
import { useConnection, useUserInfo } from '../../../../connection/Application';
import { PerformanceCycle } from '../../../../entity/PerformanceCycle';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import {
  AlignLeftOutlined,
  ArrowRightOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  ExclamationCircleOutlined,
  RetweetOutlined,
  StarFilled,
} from '@ant-design/icons';
import { Criteria } from '../review-settings/reviewQuestions';
import { RichText } from '../../../../components/RichText/RichText';
import ReactHtmlParser from '../../../../utils/SafeHtmlParser';
import { PageHeader } from '@ant-design/pro-layout';
import dayjs from 'dayjs';
const { confirm } = Modal;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

export interface InternalReviewModel {
  criteriaSet?: Criteria[];
  overallComment?: string;
  submitTime?: string;
  cycle?: number;
  submittingUser?: number;
}

const regex = /(<([^>]+)>)/gi;

const getObjectionStatusMeta = (status?: string) => {
  const normalizedStatus = status?.toLowerCase();

  if (!normalizedStatus) {
    return null;
  }

  return {
    color:
      normalizedStatus === 'accepted'
        ? 'green'
        : normalizedStatus === 'rejected'
        ? 'red'
        : 'orange',
    label: normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1),
  };
};

const InternalCustomer: FC = () => {
  const [cycle, setCycle] = useQueryParam<number | undefined>('cycle');
  const [targetUserId] = useQueryParam('userId', NumberParam);
  const cycleRef = useRef(cycle);
  const user = useUserInfo();
  const [cycles, setCycles] = useState<PerformanceCycle[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [tableData2, setTableData2] = useState<any[]>([]);
  const connection = useConnection();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [showContent, setShowContent] = useState<boolean>(true);
  const [submitData, setSubmitData] = useState<InternalReviewModel>();
  const [record, setRecord] = useState<any>();
  const [fetching, setFetching] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('1');
  const selectedCycle = useMemo(
    () => cycles.find((item) => +item.id === +(cycle ?? 0)),
    [cycle, cycles],
  );
  const isIcrObjectionEnabled = selectedCycle?.icrObjection ?? false;
  const shouldShowObjectionActionColumn = useMemo(
    () =>
      isIcrObjectionEnabled &&
      Array.isArray(tableData2) &&
      tableData2.some(
        (item: any) =>
          item.isLead &&
          item.status === 'approved' &&
          !item.objectionStatus &&
          !item.isScorePublished,
      ),
    [isIcrObjectionEnabled, tableData2],
  );

  useEffect(() => {
    if (targetUserId) {
      setActiveTab('2');
    }
  }, [targetUserId]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const userIdToFetch = targetUserId || user?.userId;
    if (cycle && userIdToFetch) {
      connection
        .get(`goals/internal-customer/${userIdToFetch}/${cycle}`)
        .then((users: any) => {
          if (!mounted) return;
          setTableData(
            users.reviewingTeams.map((e: any) => ({
              ...e,
              key: e.id,
              mode: targetUserId && targetUserId !== user?.userId ? 'view' : e.mode,
            })),
          );
          setTableData2(
            users.reviewsForMe.map((e: any) => ({
              ...e,
              key: e.id,
              criteriaSet: {
                ...e.criteriaSet,
                criterias: e.criteriaSet?.criterias.map((c: any) => ({
                  ...c,
                  answer: ((c?.answer ?? 0) / 100) * c.type ?? 5,
                })),
              },
            })),
          );
        })
        .finally(() => {
          if (mounted) setLoading(false);
        });
    } else {
      setLoading(false);
    }
    return () => {
      mounted = false;
      // ensure the beforeunload guard is removed if user navigates away while modal open
      window.onbeforeunload = null;
    };
  }, [connection, cycle, user?.userId, targetUserId, fetching]);

  const [objectionModalVisible, setObjectionModalVisible] = useState<boolean>(false);
  const [objectionReason, setObjectionReason] = useState<string>('');

  const objectAnswer = useCallback(() => {
    if (!isIcrObjectionEnabled) {
      return;
    }
    setLoading(true);
    connection
      .patch(`goals/internal-customers/${record?.id}/object`, {
        objectionReason: objectionReason,
      })
      .then(() => {
        setFetching(!fetching);
        setObjectionModalVisible(false);
        setObjectionReason('');
      })
      .catch((e) => {
        setLoading(false);
        console.error(e);
      });
  }, [connection, fetching, isIcrObjectionEnabled, objectionReason, record?.id]);

  const columns = useMemo(() => {
    return [
      {
        title: 'Internal Customer Name',
        dataIndex: 'teamName',
        key: 'teamName',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space>
            <UserAvatar type={record.itemType} src={record?.reviewer?.image ?? ''} />
            <Link
              onClick={() => {
                setRecord(record);
                window.onbeforeunload = function () {
                  return 'Data will be lost if you leave the page, are you sure?';
                };
                setModalVisible(true);
                setSubmitData({
                  criteriaSet: record?.criteriaSet?.criterias,
                  overallComment: record?.overallComment ?? '',
                  submitTime: '',
                  cycle: cycle,
                  submittingUser: 0,
                });
              }}>
              {record?.reviewer?.name && `${record?.reviewer?.name ?? ''} `}
              {record?.reviewer?.name && <ArrowRightOutlined />}
              {` ${text}`}
            </Link>
          </Space>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space>
            {(record.status == '' || record.status == 'not submitted') && (
              <Tag color={'blue'}>Not submitted</Tag>
            )}
            {record.status == 'drafted' && <Tag>Drafted</Tag>}
            {record.status == 'submitted' && <Tag color={'yellow'}>Submitted</Tag>}
            {record.status == 'approved' && <Tag color={'green'}>Approved</Tag>}
            {record.status == 'rejected' && <Tag color={'red'}>Rejected</Tag>}
            {record.status == 'recalled' && <Tag color={'orange'}>Recalled</Tag>}
          </Space>
        ),
      },
      {
        title: 'Score',
        dataIndex: 'marks',
        key: 'marks',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space>
            {!(record.status == '' || record.status == 'not submitted') && (
              <Badge
                count={
                  record.criteriaSet.criterias.reduce(
                    (x: any, y: any) => x + (y.answer / y.type) * 100,
                    0,
                  ) /
                    record.criteriaSet.criterias.length +
                  '%'
                }
                showZero
                overflowCount={120}
                style={{ fontSize: '16px' }}
                color={
                  record.criteriaSet.criterias.reduce(
                    (x: any, y: any) => x + (y.answer / y.type) * 100,
                    0,
                  ) /
                    record.criteriaSet.criterias.length >
                  85
                    ? '#52c41a'
                    : record.criteriaSet.criterias.reduce(
                        (x: any, y: any) => x + (y.answer / y.type) * 100,
                        0,
                      ) /
                        record.criteriaSet.criterias.length >
                      60
                    ? '#faad14'
                    : ''
                }
              />
              // <Progress
              //   type="circle"
              //   percent={
              //     record.criteriaSet.criterias.reduce(
              //       (x: any, y: any) => x + (y.answer / y.type) * 100,
              //       0,
              //     ) / record.criteriaSet.criterias.length
              //   }
              //   width={65}
              //   format={() =>
              //     `${
              //       record.criteriaSet.criterias.reduce(
              //         (x: any, y: any) => x + (y.answer / y.type) * 100,
              //         0,
              //       ) / record.criteriaSet.criterias.length
              //     }%`
              //   }
              // />
            )}
          </Space>
        ),
      },
    ];
  }, [cycle]);

  useEffect(() => {
    const token = connection.hasSession();
    if (token) {
      setLoading(true);
      connection
        .get<{ performanceCycle: PerformanceCycle[]; closestCycle: PerformanceCycle }>('cycles')
        .then((value) => {
          setCycles(value.performanceCycle);
          if (cycleRef.current === undefined) {
            setCycle(value.closestCycle.id, 'replace');
          }
        })
        .catch(() => setLoading(false));
    }
  }, [connection, setCycle]);

  const createInternalReview = useCallback(
    (submitData: InternalReviewModel) => {
      setLoading(true);
      if (submitData?.criteriaSet?.length !== 0) {
        connection
          .patch(`goals/internalReviewAnswer/${record?.id}`, {
            answers: submitData?.criteriaSet?.map((e) => ({
              id: e.id,
              answer: ((e?.answer ?? 0) / e.type) * 100,
              comment: e.comment,
            })),
            overallComment: submitData?.overallComment,
            userId: user?.userId,
          })
          .then(() => {
            window.onbeforeunload = null;
            setModalVisible(false);
            setSubmitData({
              criteriaSet: [],
              overallComment: '',
              submitTime: '',
              cycle: cycle,
              submittingUser: 0,
            });
            setFetching(!fetching);
            // setRecord(undefined);
          })
          .catch(() => setLoading(false));
      }
    },
    [connection, cycle, fetching, record?.id, user?.userId],
  );

  const showApproveModal = useCallback(() => {
    confirm({
      title: 'Submitting your review',
      icon: <ExclamationCircleOutlined />,
      content: `If you submit you won't be able to change`,
      okText: `Submit`,
      centered: true,
      okType: 'primary',
      cancelText: 'Cancel',
      onOk() {
        setSubmitData({
          ...submitData,
          cycle: cycle,
          submittingUser: user?.userId,
          submitTime: dayjs().format('MMM DD, YYYY h:mm a'),
        });
        createInternalReview({
          ...submitData,
          cycle: cycle,
          submittingUser: user?.userId,
          submitTime: dayjs().format('MMM DD, YYYY h:mm a'),
        });
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [createInternalReview, cycle, submitData, user?.userId]);

  const columns2 = useMemo(() => {
    const baseColumns = [
      {
        title: 'Internal Customer Name',
        dataIndex: 'teamName',
        key: 'teamName',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space>
            <UserAvatar type={record.itemType} src={record?.reviewer?.image ?? ''} />
            <Link
              onClick={() => {
                setRecord(record);
                setModalVisible(true);
                setSubmitData({
                  criteriaSet: record?.criteriaSet?.criterias,
                  overallComment: record?.overallComment ?? '',
                  submitTime: '',
                  cycle: cycle,
                  submittingUser: 0,
                });
              }}>
              {record?.reviewer?.name && `${record?.reviewer?.name ?? ''} `}
              {record?.reviewer?.name && <ArrowRightOutlined />}
              {` ${text}`}
            </Link>
          </Space>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space direction="vertical" size={0}>
            <Space>
              {(record.status == '' || record.status == 'not submitted') && (
                <Tag color={'blue'}>Not submitted</Tag>
              )}
              {record.status == 'drafted' && <Tag>Drafted</Tag>}
              {record.status == 'submitted' && <Tag color={'yellow'}>Submitted</Tag>}
              {record.status == 'approved' && <Tag color={'green'}>Approved</Tag>}
              {record.status == 'rejected' && <Tag color={'red'}>Rejected</Tag>}
              {record.status == 'recalled' && <Tag color={'orange'}>Recalled</Tag>}
            </Space>
            {record.objectionStatus && (
              <Tag
                color={
                  record.objectionStatus === 'Pending'
                    ? 'warning'
                    : record.objectionStatus === 'Accepted'
                    ? 'success'
                    : 'error'
                }
                style={{ marginTop: 4 }}>
                Objection {record.objectionStatus}
              </Tag>
            )}
          </Space>
        ),
      },
      {
        title: 'Score',
        dataIndex: 'marks',
        key: 'marks',
        // eslint-disable-next-line react/display-name
        render: (text: string, record: any) => (
          <Space>
            {!(record.status == '' || record.status == 'not submitted') && (
              <Badge
                count={
                  record.criteriaSet.criterias.reduce(
                    (x: any, y: any) => x + (y.answer / y.type) * 100,
                    0,
                  ) /
                    record.criteriaSet.criterias.length +
                  '%'
                }
                showZero
                overflowCount={120}
                style={{ fontSize: '16px' }}
                color={
                  record.criteriaSet.criterias.reduce(
                    (x: any, y: any) => x + (y.answer / y.type) * 100,
                    0,
                  ) /
                    record.criteriaSet.criterias.length >
                  85
                    ? '#52c41a'
                    : record.criteriaSet.criterias.reduce(
                        (x: any, y: any) => x + (y.answer / y.type) * 100,
                        0,
                      ) /
                        record.criteriaSet.criterias.length >
                      60
                    ? '#faad14'
                    : ''
                }
              />
            )}
          </Space>
        ),
      },
    ];
    return shouldShowObjectionActionColumn
      ? [
          ...baseColumns,
          {
            title: 'Actions',
            key: 'actions',
            // eslint-disable-next-line react/display-name
            render: (text: string, record: any) => (
              <Space>
                {record.isLead &&
                  record.status === 'approved' &&
                  !record.objectionStatus &&
                  !record.isScorePublished &&
                  isIcrObjectionEnabled && (
                    <Button
                      size="small"
                      danger
                      onClick={() => {
                        setRecord(record);
                        setObjectionModalVisible(true);
                      }}>
                      Object
                    </Button>
                  )}
              </Space>
            ),
          },
        ]
      : baseColumns;
  }, [cycle, isIcrObjectionEnabled, shouldShowObjectionActionColumn]);

  const draftReview = useCallback(() => {
    setLoading(true);
    connection
      .patch(`goals/internal_review/answer/draft/${record.id}`, {
        answers: submitData?.criteriaSet?.map((e) => ({
          id: e.id,
          answer: ((e?.answer ?? 0) !== 0 ? (e?.answer ?? 0) / e?.type : 0) * 100,
          comment: e?.comment ?? '',
        })),
        overallComment: submitData?.overallComment ?? '',
      })
      .then(() => {
        window.onbeforeunload = null;
        setModalVisible(false);
        setSubmitData({
          criteriaSet: [],
          overallComment: '',
          submitTime: '',
          cycle: cycle,
          submittingUser: 0,
        });
        setFetching(!fetching);
        setRecord(undefined);
      })
      .finally(() => setLoading(false));
  }, [
    connection,
    cycle,
    fetching,
    record?.id,
    submitData?.criteriaSet,
    submitData?.overallComment,
  ]);

  const showDraftModal = useCallback(() => {
    confirm({
      title: 'Draft your review',
      icon: <ExclamationCircleOutlined />,
      content: `Draft your review changes`,
      okText: `Draft`,
      centered: true,
      okType: 'primary',
      cancelText: 'Cancel',
      onOk() {
        setSubmitData({
          ...submitData,
          cycle: cycle,
          submittingUser: user?.userId,
          submitTime: dayjs().format('MMM DD, YYYY h:mm a'),
        });
        draftReview();
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [cycle, draftReview, submitData, user?.userId]);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Internal Customer Reviews"
        subTitle="Manage Internal Customer reviews"
        // breadcrumb={{
        //   routes: [
        //     {
        //       path: 'goals',
        //       breadcrumbName: `Internal Customer Reviews`,
        //     },
        //   ],
        // }}
      />
      <WebUxPageContent>
        <WebUxPageItem>
          <FlexBox>
            <CycleToggle
              value={parseInt(`${cycle}`)}
              onChange={(value) => setCycle(value, 'replace')}
              data={cycles}
            />
          </FlexBox>
          <Spin spinning={loading}>
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane tab="Reviews By Me" key="1">
                <Table
                  size="small"
                  rowKey={(record: any, index) => record?.key ?? `${record?.id}-${index}`}
                  columns={columns}
                  dataSource={tableData}
                  expandable={{ indentSize: 0 }}
                  pagination={false}
                />
              </TabPane>
              <TabPane tab="Reviews For Me" key="2">
                <Table
                  size="small"
                  rowKey={(record: any, index) => record?.key ?? `${record?.id}-${index}`}
                  columns={columns2}
                  dataSource={tableData2}
                  expandable={{ indentSize: 0 }}
                  pagination={false}
                />
              </TabPane>
            </Tabs>
          </Spin>
        </WebUxPageItem>
      </WebUxPageContent>
      <Modal
        title="Object Internal Customer Review"
        open={objectionModalVisible}
        onCancel={() => {
          setObjectionModalVisible(false);
          setObjectionReason('');
        }}
        onOk={objectAnswer}
        okButtonProps={{ disabled: !objectionReason.trim() }}
        confirmLoading={loading}>
        <RichText
          placeholder="Reason for Objection"
          value={objectionReason}
          onChange={(value) => setObjectionReason(value)}
        />
      </Modal>
      <Modal
        open={modalVisible}
        destroyOnClose
        onCancel={() => {
          setRecord(undefined);
          setSubmitData({
            criteriaSet: [],
            overallComment: '',
            submitTime: '',
            cycle: cycle,
            submittingUser: 0,
          });
          window.onbeforeunload = null;
          setModalVisible(false);
        }}
        footer={
          record?.status === 'rejected'
            ? [
                <Button
                  key="cancel"
                  onClick={() => {
                    setRecord(undefined);
                    setSubmitData({
                      criteriaSet: [],
                      overallComment: '',
                      submitTime: '',
                      cycle: cycle,
                      submittingUser: 0,
                    });
                    window.onbeforeunload = null;
                    setModalVisible(false);
                    // events.emit('PEER_REVIEW_CLOSE');
                  }}>
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  hidden={record?.mode === 'view'}
                  disabled={
                    submitData?.overallComment === '<p><br></p>' ||
                    submitData?.overallComment === '' ||
                    submitData?.criteriaSet?.length === 0 ||
                    submitData?.criteriaSet?.findIndex(
                      (e) => e.answer === 0 || e.answer === null || e.answer === undefined,
                    ) !== -1
                  }
                  type="primary"
                  loading={loading}
                  onClick={() => showApproveModal()}>
                  Submit
                </Button>,
              ]
            : [
                <Button
                  key="cancel"
                  onClick={() => {
                    setRecord(undefined);
                    setSubmitData({
                      criteriaSet: [],
                      overallComment: '',
                      submitTime: '',
                      cycle: cycle,
                      submittingUser: 0,
                    });
                    window.onbeforeunload = null;
                    setModalVisible(false);
                    // events.emit('PEER_REVIEW_CLOSE');
                  }}>
                  Cancel
                </Button>,
                <Button
                  key="draft"
                  hidden={record?.mode === 'view'}
                  type="default"
                  loading={loading}
                  onClick={() => showDraftModal()}>
                  Draft
                </Button>,
                <Button
                  key="submit"
                  hidden={record?.mode === 'view'}
                  disabled={
                    submitData?.overallComment === '<p><br></p>' ||
                    submitData?.overallComment === '' ||
                    submitData?.criteriaSet?.length === 0 ||
                    submitData?.criteriaSet?.findIndex(
                      (e) => e.answer === 0 || e.answer === null || e.answer === undefined,
                    ) !== -1
                  }
                  type="primary"
                  loading={loading}
                  onClick={() => showApproveModal()}>
                  Submit
                </Button>,
              ]
        }>
        {record && (
          <>
            <Row style={{ marginTop: '3%' }}>
              <UserAvatar size={80} />
              <Col style={{ marginLeft: '3%', marginTop: '1%' }}>
                <Row style={{ alignItems: 'center' }}>
                  <Space>
                    <Title level={3} style={{ margin: 0 }}>
                      {record?.teamName}
                    </Title>
                    <div>
                      <Tag color={record?.teamType?.color}>{record?.teamType?.name}</Tag>
                    </div>
                  </Space>
                </Row>
                <Text type="secondary">{record?.teamDescription}</Text>
              </Col>
            </Row>
            <Divider style={{ marginTop: '2%' }} />
            <div style={{ marginLeft: '1%' }}>
              {record?.status === 'rejected' &&
                (record as any) &&
                ((record as any).rejectionReason?.replace?.(regex, '').length ||
                  record?.overallComment?.replace?.(regex, '').length) && (
                  <div style={{ marginBottom: 16 }}>
                    <Alert
                      type="error"
                      showIcon
                      message="This feedback has been rejected"
                      description={
                        <div>
                          <Text strong>Reason:</Text>
                          <div>
                            {ReactHtmlParser(
                              ((record as any).rejectionReason &&
                              (record as any).rejectionReason.replace(regex, '').length
                                ? (record as any).rejectionReason
                                : record?.overallComment) || '',
                            )}
                          </div>
                        </div>
                      }
                    />
                  </div>
                )}
              {record?.status === 'recalled' && (record as any).recallReason && (
                <div style={{ marginBottom: 16 }}>
                  <Alert
                    type="warning"
                    showIcon
                    message="This feedback has been recalled"
                    description={
                      <div>
                        <Text strong>Recall Reason:</Text>
                        <div>{ReactHtmlParser((record as any).recallReason || '')}</div>
                      </div>
                    }
                  />
                </div>
              )}
              {record?.objectionStatus && record?.status !== 'recalled' && (
                <div style={{ marginBottom: 16 }}>
                  <Alert
                    type={
                      record.objectionStatus === 'Pending'
                        ? 'warning'
                        : record.objectionStatus === 'Accepted'
                        ? 'success'
                        : 'error'
                    }
                    showIcon
                    message={`Objection ${record.objectionStatus}`}
                    description={
                      <div>
                        <Text strong>Reason:</Text>
                        <div>{ReactHtmlParser(record.objectionReason || '')}</div>
                      </div>
                    }
                  />
                </div>
              )}
              <ReviewPreview
                mode={record?.mode}
                criterias={record?.criteriaSet?.criterias}
                onChange={(val) => setSubmitData({ ...submitData, criteriaSet: val })}
              />
            </div>
            <Space style={{ marginTop: '2%' }}>
              <StarFilled style={{ color: 'red', fontSize: 7, paddingRight: 5 }} />
              <AlignLeftOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              <Text type="secondary">Overall Comment</Text>
            </Space>
            <Divider orientation="left" style={{ marginTop: 0, marginBottom: 0 }} />
            <div style={{ marginTop: '2%' }}>
              {record?.mode === 'edit' && (
                // <Form
                //   form={form}
                //   onFinish={
                //     (e) => console.log(e)
                //     // setSubmitData({ ...submitData, overallComment: e })
                //   }>
                //   <Form.Item
                //     name="comment"
                //     rules={[
                //       { required: true, whitespace: true, message: 'Comment is required' },
                //       { max: 10000, message: 'Only 10000 characters are allowed.' },
                //     ]}>
                //     <RichText
                //       placeholder={'Add your overall comment'}
                //       value={submitData?.overallComment}
                //       maxLength={10000}
                //       onChange={form.submit}
                //     />
                //   </Form.Item>
                // </Form>
                // <TextArea
                //   placeholder="Add your overall comment"
                //   allowClear
                //   autoSize={{ minRows: 3, maxRows: 5 }}
                //   value={submitData?.overallComment}
                //   onChange={(e) => setSubmitData({ ...submitData, overallComment: e.target.value })}
                // />
                <RichText
                  placeholder={'Add your overall comment'}
                  value={submitData?.overallComment}
                  maxLength={10000}
                  onChange={(e) => setSubmitData({ ...submitData, overallComment: e })}
                />
              )}
              {record?.mode === 'view' && (
                <Typography>
                  {record?.overallComment && !!record?.overallComment?.replace(regex, '').length
                    ? ReactHtmlParser(record?.overallComment)
                    : ''}
                </Typography>
              )}
            </div>
            {(record?.internalCustomerReviewHistory?.length ?? 0) !== 0 && (
              <>
                <Row style={{ alignItems: 'center', marginTop: 20 }}>
                  <Space>
                    <Title level={5}>Activity</Title>
                    <RetweetOutlined style={{ fontSize: 20 }} />
                  </Space>
                </Row>
                <Link onClick={() => setShowContent(!showContent)}>
                  <Space className="div_hover">
                    <Text>Show Activities</Text>
                    {!showContent ? (
                      <CaretDownOutlined style={{ marginTop: 7, color: 'rgba(0,0,0,0.7)' }} />
                    ) : (
                      <CaretUpOutlined style={{ marginTop: 7, color: 'rgba(0,0,0,0.7)' }} />
                    )}
                  </Space>
                </Link>
              </>
            )}
            {(record?.internalCustomerReviewHistory?.length ?? 0) !== 0 &&
              showContent &&
              record?.internalCustomerReviewHistory?.map((item: any, key: any) => {
                const objectionStatusMeta = getObjectionStatusMeta(item.objectionStatus);

                return (
                  <div key={key} className="borderBox">
                    <Row>
                      <Space>
                        <UserAvatar src={item?.image} />
                        <Text strong>{item?.name}</Text>
                        {/*<Text type="secondary">{item.date + ' ' + item.time}</Text>*/}
                      </Space>
                    </Row>
                    <Row>
                      <Text strong>
                        Status:
                        {(record.status == '' || record.status == 'not submitted') && (
                          <Tag color={'blue'}>Not submitted</Tag>
                        )}
                        {item.status == 'submitted' && <Tag color={'yellow'}>Submitted</Tag>}
                        {item.status == 'approved' && <Tag color={'green'}>Approved</Tag>}
                        {item.status == 'recalled' && <Tag color={'orange'}>Recalled</Tag>}
                        {item.status == 'rejected' && <Tag color={'red'}>Rejected</Tag>}
                      </Text>
                      <Text type="secondary">{item.date}</Text>
                    </Row>
                    {item?.status === 'recalled' && (
                      <div style={{ marginBottom: 10 }}>
                        <Row style={{ marginLeft: 20 }}>
                          <Text strong>Recall Reason</Text>
                        </Row>
                        <Row style={{ marginLeft: 20 }}>
                          <Text type="secondary">
                            {item.recallReason && !!item.recallReason?.replace(regex, '').length
                              ? ReactHtmlParser(item.recallReason)
                              : ''}
                          </Text>
                        </Row>
                      </div>
                    )}
                    {activeTab !== '1' && item?.objectionReason && (
                      <div style={{ marginBottom: 10 }}>
                        <Row style={{ marginLeft: 20 }}>
                          <Space>
                            <Text strong>Objection</Text>
                            {objectionStatusMeta && (
                              <Tag color={objectionStatusMeta.color}>
                                {objectionStatusMeta.label}
                              </Tag>
                            )}
                          </Space>
                        </Row>
                        <Row style={{ marginLeft: 20 }}>
                          <Text type="secondary">
                            {item.objectionReason &&
                            !!item.objectionReason?.replace(regex, '').length
                              ? ReactHtmlParser(item.objectionReason)
                              : ''}
                          </Text>
                        </Row>
                      </div>
                    )}
                    {item?.status === 'submitted' &&
                      item.history?.map((e: any, index1: any) => {
                        return (
                          <div key={index1}>
                            <Row style={{ marginTop: 20 }}>
                              <Text type="secondary">{`Submitted ${e.criteriaName}`}</Text>
                            </Row>
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Answer</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Rate
                                    value={(e.newAnswer / 100) * e.criteriaType ?? 0}
                                    character={<StarFilled style={{ fontSize: 15 }} />}
                                    count={e.type}
                                    disabled={true}
                                  />
                                </Row>
                              </div>
                            )}
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text type="secondary">
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                            {e.newAnswer === 0 && e.oldAnswer === 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text type="secondary">
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {item?.status !== 'submitted' &&
                      item.history?.map((e: any, index1: any) => {
                        return (
                          <div key={index1}>
                            <Row style={{ marginTop: 10 }}>
                              <Text type="secondary">{`Updated ${e.criteriaName}`}</Text>
                            </Row>
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Answer</Text>
                                </Row>
                                <Row
                                  style={{ marginLeft: 20, display: 'flex', alignItems: 'center' }}>
                                  <Rate
                                    value={(e.oldAnswer / 100) * e.criteriaType ?? 0}
                                    character={<StarFilled style={{ fontSize: 15 }} />}
                                    count={e.type}
                                    disabled={true}
                                  />
                                  <ArrowRightOutlined
                                    style={{ paddingTop: 6, paddingLeft: 15, paddingRight: 15 }}
                                  />
                                  <Rate
                                    value={(e.newAnswer / 100) * e.criteriaType ?? 0}
                                    character={<StarFilled style={{ fontSize: 15 }} />}
                                    count={e.type}
                                    disabled={true}
                                  />
                                </Row>
                              </div>
                            )}
                            {e.newAnswer !== 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>

                                <Row
                                  style={{ marginLeft: 20, display: 'flex', alignItems: 'center' }}>
                                  <Text type="secondary">
                                    {e.oldComment && !!e.oldComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.oldComment)
                                      : ''}
                                    {'   '}
                                  </Text>
                                  <ArrowRightOutlined
                                    style={{ paddingLeft: 15, paddingRight: 15 }}
                                  />
                                  <Text type="secondary">
                                    {'   '}
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                            {e.newAnswer === 0 && e.oldAnswer === 0 && (
                              <div>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text strong>Comment</Text>
                                </Row>
                                <Row style={{ marginLeft: 20 }}>
                                  <Text type="secondary">
                                    {e.oldComment && !!e.oldComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.oldComment)
                                      : ''}
                                    {'   '}
                                  </Text>
                                  <ArrowRightOutlined
                                    style={{ paddingLeft: 15, paddingRight: 15 }}
                                  />
                                  <Text type="secondary">
                                    {'   '}
                                    {e.newComment && !!e.newComment?.replace(regex, '').length
                                      ? ReactHtmlParser(e.newComment)
                                      : ''}
                                  </Text>
                                </Row>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                );
              })}
          </>
        )}
      </Modal>
    </WebUxPage>
  );
};

export default InternalCustomer;
